/**
 * RecommendationList — Sprint 3C.
 *
 * The canonical UI for the Unified Recommendation Engine.
 * Consumes `/api/recommendations` (aggregator over producers).
 *
 * PO template (verbatim):
 *
 *   Status         ORDER
 *   What           Clear Diesel
 *   Why            Estimated 2.3 days remaining
 *   Urgency        🔴 High
 *   Recommended By Tomorrow 10:00 AM
 *   Confidence     94%  ·  ✓ Inventory ✓ Consumption ✓ Price ✓ Deliveries ✓ Reconciliation
 *
 * Used on:
 *   • Morning Brief (replaces the legacy "What should we order?" widget
 *     and the standalone Tank Replenishment card).
 *   • Purchasing workspace (unified feed).
 *   • (Sprint 3D) OCC compact chip.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ChartLineUp, ArrowsClockwise, CheckCircle, Circle, Info,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// Visual palette
// ---------------------------------------------------------------------------
const STATUS_META = {
  ORDER: { label: "ORDER", pill: "bg-slate-900 text-white",
           card:  "border-slate-300",  accent: "bg-slate-900" },
  WATCH: { label: "WATCH", pill: "bg-amber-100 text-amber-900 border border-amber-300",
           card:  "border-amber-200",  accent: "bg-amber-500" },
  WAIT:  { label: "WAIT",  pill: "bg-slate-100 text-slate-700 border border-slate-300",
           card:  "border-slate-200",  accent: "bg-slate-400" },
};
const URGENCY_META = {
  HIGH:   { label: "🔴 High",   pill: "bg-rose-100 text-rose-900 border border-rose-300" },
  MEDIUM: { label: "🟡 Medium", pill: "bg-amber-100 text-amber-900 border border-amber-300" },
  LOW:    { label: "🟢 Low",    pill: "bg-emerald-100 text-emerald-900 border border-emerald-300" },
};
const SIGNAL_LABELS = {
  inventory:      "Inventory",
  consumption:    "Consumption",
  reconciliation: "Reconciliation",
  price:          "Price",
  deliveries:     "Deliveries",
};
const PRODUCER_LABEL = {
  tank_replenishment:      "Tank Forecast",
  purchasing_intelligence: "Purchasing Signals",
};


export default function RecommendationList({
  compact = false,
  title,
  testidPrefix = "recs",
  showUrgencies,   // optional Array<"HIGH"|"MEDIUM"|"LOW"> — filter surface
  emptyMessage,    // optional override for the empty-state copy
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/recommendations");
      setData(d);
    } catch (e) {
      toast.error("Could not load recommendations.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`${testidPrefix}-loading`}>
        <p className="text-sm text-slate-500">Loading recommendations…</p>
      </section>
    );
  }

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid={`${testidPrefix}-card`}
    >
      {/* Header */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-baseline gap-3">
        <ChartLineUp size={16} className="text-slate-500" weight="fill" />
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-slate-900">
            {title || "Operational Recommendations"}
          </h2>
          <p className="text-xs text-slate-500 italic" data-testid={`${testidPrefix}-contract`}>
            {data.insight_contract}
          </p>
        </div>
        <HeaderCounts counts={data.counts} testid={`${testidPrefix}-counts`} />
        <button
          onClick={load}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid={`${testidPrefix}-refresh`}
        >
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {/* Body */}
      {(() => {
        const raw = data.items || [];
        const items = showUrgencies?.length
          ? raw.filter((r) => showUrgencies.includes(r.urgency))
          : raw;
        if (items.length === 0) {
          return <EmptyState producers={data.producers_used} message={emptyMessage} testid={testidPrefix} />;
        }
        return (
          <ul className="divide-y divide-slate-100" data-testid={`${testidPrefix}-list`}>
            {items.map((r) => (
              <RecommendationRow
                key={r.id}
                r={r}
                compact={compact}
                testidPrefix={testidPrefix}
                onDecisionChanged={load}
              />
            ))}
          </ul>
        );
      })()}

      {/* Producer status */}
      <ProducerStatus data={data} testid={`${testidPrefix}-producers`} />
    </section>
  );
}


// ---------------------------------------------------------------------------
// Header counts
// ---------------------------------------------------------------------------
function HeaderCounts({ counts, testid }) {
  if (!counts || counts.total === 0) return null;
  return (
    <div className="text-[11px] text-slate-600 flex items-center gap-2" data-testid={testid}>
      {counts.HIGH > 0 ? <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-rose-600" />{counts.HIGH} high</span> : null}
      {counts.MEDIUM > 0 ? <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{counts.MEDIUM} medium</span> : null}
      {counts.LOW > 0 ? <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />{counts.LOW} low</span> : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Row · standard shape rendered as PO template
// ---------------------------------------------------------------------------
function RecommendationRow({ r, compact, testidPrefix, onDecisionChanged }) {
  const s = STATUS_META[r.status] || STATUS_META.WATCH;
  const u = URGENCY_META[r.urgency] || URGENCY_META.LOW;
  const decision = r.decision?.decision;   // accept · defer · dismiss · undefined
  return (
    <li
      className={`px-5 py-4 ${decision === "dismiss" ? "opacity-60" : ""}`}
      data-testid={`${testidPrefix}-row-${r.product_code}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-1 shrink-0 self-stretch rounded ${s.accent}`} />
        <div className="flex-1">
          {/* Top row: status pill · What · urgency pill · decision pill */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className={`text-[10px] font-bold tracking-wide rounded px-2 py-0.5 ${s.pill}`}
              data-testid={`${testidPrefix}-status-${r.product_code}`}
            >
              {s.label}
            </span>
            <div className="text-sm font-semibold text-slate-900" data-testid={`${testidPrefix}-what-${r.product_code}`}>
              {r.what}
            </div>
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${u.pill}`}
              data-testid={`${testidPrefix}-urgency-${r.product_code}`}
            >
              {u.label}
            </span>
            {decision ? (
              <DecisionPill decision={r.decision} testid={`${testidPrefix}-decision-${r.product_code}`} />
            ) : null}
          </div>

          {/* Field grid — the PO template */}
          <dl className={`mt-2 grid ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-6"} gap-x-4 gap-y-1 text-[13px]`}>
            <Field label="Why" span={compact ? 1 : 3} testid={`${testidPrefix}-why-${r.product_code}`}>
              {r.why}
            </Field>
            <Field label="Recommended By" span={compact ? 1 : 3} testid={`${testidPrefix}-when-${r.product_code}`}>
              {r.recommended_by?.human || "—"}
            </Field>
          </dl>

          {/* Confidence block — redesigned per PO 2026-08-01 · prominent + producer-grounded */}
          <ConfidenceBlock
            confidence={r.confidence}
            producers={r.producers}
            testid={`${testidPrefix}-confidence-${r.product_code}`}
          />

          {/* Decision actions · Accept · Defer · Dismiss */}
          {!compact ? (
            <DecisionActions
              recommendationId={r.id}
              currentDecision={r.decision || null}
              testidPrefix={`${testidPrefix}-actions-${r.product_code}`}
              onChange={onDecisionChanged}
            />
          ) : null}

          {/* Contextual per-recommendation history (ARC-010) —
              deliberately attached to the card, never a standalone
              workspace (PO 2026-08-01). */}
          {!compact ? (
            <RecommendationHistory
              productCode={r.product_code}
              testidPrefix={`${testidPrefix}-history-${r.product_code}`}
            />
          ) : null}
        </div>
      </div>
    </li>
  );
}


function Field({ label, span = 1, children, testid }) {
  const spanCls = { 1: "md:col-span-1", 2: "md:col-span-2", 3: "md:col-span-3",
                    4: "md:col-span-4", 5: "md:col-span-5", 6: "md:col-span-6" }[span] || "md:col-span-1";
  return (
    <div className={spanCls}>
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="text-slate-800" data-testid={testid}>{children || "—"}</dd>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Confidence block · redesigned per PO 2026-08-01
//
//   Confidence
//   44%
//   Based on
//   ✓ Tank Forecast
//   ✓ Purchasing Signals
//
// Signal-level detail (Inventory · Consumption · Reconciliation · Price ·
// Deliveries) is preserved as a secondary caption so operators see *why*
// confidence improves as more signals are wired in.
// ---------------------------------------------------------------------------
function ConfidenceBlock({ confidence, producers, testid }) {
  if (!confidence) return null;
  const entries = Object.entries(confidence.signals || {});
  const active   = entries.filter(([, v]) => v).map(([k]) => k);
  const inactive = entries.filter(([, v]) => !v).map(([k]) => k);
  const producerList = producers && producers.length ? producers : [];

  return (
    <div
      className="mt-3 rounded-md border border-slate-200 bg-slate-50/70 px-4 py-3 flex flex-wrap items-start gap-x-8 gap-y-2"
      data-testid={testid}
    >
      {/* Big score */}
      <div className="shrink-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Confidence
        </div>
        <div
          className="text-2xl font-semibold text-slate-900 tabular-nums"
          data-testid={`${testid}-score`}
        >
          {confidence.score ?? "—"}
          {confidence.score !== null && confidence.score !== undefined ? (
            <span className="text-sm font-medium text-slate-500 ml-0.5">%</span>
          ) : null}
        </div>
      </div>

      {/* Based on · producer list */}
      {producerList.length ? (
        <div className="shrink-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Based on
          </div>
          <ul
            className="mt-1 space-y-0.5 text-[12px] text-slate-800"
            data-testid={`${testid}-producers`}
          >
            {producerList.map((p) => (
              <li key={p} className="inline-flex items-center gap-1.5 mr-4">
                <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                {PRODUCER_LABEL[p] || p}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Signal-level detail · caption row */}
      <div className="flex-1 min-w-[240px]">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Signals
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600">
          {active.map((k) => (
            <span key={k} className="inline-flex items-center gap-1">
              <CheckCircle size={11} weight="fill" className="text-emerald-600" />
              {SIGNAL_LABELS[k] || k}
            </span>
          ))}
          {inactive.map((k) => (
            <span key={k} className="inline-flex items-center gap-1 text-slate-400">
              <Circle size={11} weight="regular" />
              <s>{SIGNAL_LABELS[k] || k}</s>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Decision UI · Accept · Defer · Dismiss (Sprint 3D)
// ---------------------------------------------------------------------------
const DECISION_META = {
  accept:  { label: "Accepted",  pill: "bg-emerald-100 text-emerald-900 border border-emerald-300" },
  defer:   { label: "Deferred",  pill: "bg-amber-100 text-amber-900 border border-amber-300" },
  dismiss: { label: "Dismissed", pill: "bg-slate-200 text-slate-700 border border-slate-300" },
};

function DecisionPill({ decision, testid }) {
  const meta = DECISION_META[decision.decision];
  if (!meta) return null;
  const when = decision.at
    ? new Date(decision.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";
  return (
    <span
      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.pill}`}
      data-testid={testid}
      title={`${meta.label} by ${decision.by_name || decision.by_email || "operator"}${when ? " · " + when : ""}${decision.reason ? " · " + decision.reason : ""}`}
    >
      {meta.label} · {decision.by_name || "operator"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Contextual per-recommendation history · ARC-010 · PO 2026-08-01
// "Keep the history attached to the recommendation itself."
// ---------------------------------------------------------------------------
const DECISION_HISTORY_META = {
  accept:  { label: "Accepted",  chip: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  defer:   { label: "Deferred",  chip: "bg-amber-100 text-amber-900 border-amber-300" },
  dismiss: { label: "Dismissed", chip: "bg-slate-200 text-slate-700 border-slate-300" },
};

function _formatHistoryDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(today);
  y.setDate(today.getDate() - 1);
  const isSame = (a, b) => a.toDateString() === b.toDateString();
  if (isSame(d, today)) return "Today";
  if (isSame(d, y))     return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function RecommendationHistory({ productCode, testidPrefix }) {
  const [open, setOpen]   = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recommendations/history", {
        params: { product_code: productCode, limit: 10 },
      });
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [productCode]);

  const toggle = () => {
    if (!open && items === null) load();
    setOpen((v) => !v);
  };

  return (
    <div className="mt-3 border-t border-slate-100 pt-2" data-testid={testidPrefix}>
      <button
        type="button"
        onClick={toggle}
        className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
        data-testid={`${testidPrefix}-toggle`}
      >
        History
        <span className="text-slate-400">{open ? "▲" : "▼"}</span>
      </button>
      {open ? (
        <div className="mt-2" data-testid={`${testidPrefix}-panel`}>
          {loading ? (
            <p className="text-xs text-slate-500">Loading history…</p>
          ) : items && items.length ? (
            <ul className="space-y-1.5 text-[12px] text-slate-800" data-testid={`${testidPrefix}-list`}>
              {items.map((h, i) => {
                const meta = DECISION_HISTORY_META[h.decision] || { label: h.decision, chip: "bg-slate-100 text-slate-700 border-slate-300" };
                return (
                  <li key={h.brief_date + "::" + i} className="flex items-start gap-2" data-testid={`${testidPrefix}-item-${i}`}>
                    <span className="w-20 shrink-0 text-slate-500 tabular-nums">{_formatHistoryDate(h.at || h.brief_date)}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border shrink-0 ${meta.chip}`}>
                      {meta.label}
                    </span>
                    <span className="text-slate-700 truncate">
                      {h.by_name || h.by_email || "operator"}
                      {h.reason ? <> · <span className="italic text-slate-600">"{h.reason}"</span></> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-slate-500" data-testid={`${testidPrefix}-empty`}>
              No prior decisions recorded for this product.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}


function DecisionActions({ recommendationId, currentDecision, testidPrefix, onChange }) {  const [pending, setPending] = useState(null); // { decision, needsReason }
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (decision, reasonText = "") => {
    setSaving(true);
    try {
      await api.post(`/recommendations/${encodeURIComponent(recommendationId)}/decision`, {
        decision,
        reason: reasonText || null,
      });
      toast.success(`Recommendation ${decision === "accept" ? "accepted" : decision === "defer" ? "deferred" : "dismissed"}.`);
      setPending(null);
      setReason("");
      if (onChange) onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not record decision.");
    } finally {
      setSaving(false);
    }
  };

  const buttonCls = (variant) => {
    const base = "text-[11px] font-medium px-2.5 py-1 rounded border transition-colors";
    if (variant === "accept")  return `${base} border-emerald-300 text-emerald-800 hover:bg-emerald-50`;
    if (variant === "defer")   return `${base} border-amber-300 text-amber-900 hover:bg-amber-50`;
    if (variant === "dismiss") return `${base} border-slate-300 text-slate-700 hover:bg-slate-100`;
    return base;
  };

  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={testidPrefix}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mr-2">
        {currentDecision ? "Change decision" : "Decide"}
      </div>
      <button
        type="button"
        onClick={() => setPending({ decision: "accept" })}
        disabled={saving}
        className={buttonCls("accept")}
        data-testid={`${testidPrefix}-accept`}
      >
        Accept
      </button>
      <button
        type="button"
        onClick={() => setPending({ decision: "defer" })}
        disabled={saving}
        className={buttonCls("defer")}
        data-testid={`${testidPrefix}-defer`}
      >
        Defer
      </button>
      <button
        type="button"
        onClick={() => setPending({ decision: "dismiss" })}
        disabled={saving}
        className={buttonCls("dismiss")}
        data-testid={`${testidPrefix}-dismiss`}
      >
        Dismiss
      </button>

      {/* Inline reason capture — one dialog for all three actions. */}
      {pending ? (
        <div
          className="basis-full mt-2 rounded-md border border-slate-300 bg-white p-3 flex flex-col gap-2"
          data-testid={`${testidPrefix}-reason-panel`}
        >
          <div className="text-xs text-slate-700">
            Optional reason for <b>{pending.decision}</b>. Recorded to the audit trail.
          </div>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              pending.decision === "dismiss"
                ? "e.g. supplier delivery already in transit"
                : pending.decision === "defer"
                ? "e.g. waiting on supplier confirmation"
                : "e.g. matches our order plan"
            }
            className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm bg-white focus:border-slate-500 focus:outline-none"
            data-testid={`${testidPrefix}-reason-input`}
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setPending(null); setReason(""); }}
              disabled={saving}
              className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1"
              data-testid={`${testidPrefix}-reason-cancel`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => submit(pending.decision, reason)}
              disabled={saving}
              className="text-xs font-semibold rounded bg-slate-900 text-white px-3 py-1 hover:bg-slate-800 disabled:opacity-50"
              data-testid={`${testidPrefix}-reason-submit`}
            >
              {saving ? "Saving…" : `Confirm ${pending.decision}`}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------
function EmptyState({ producers, testid, message }) {
  return (
    <div className="px-5 py-6 flex items-start gap-3" data-testid={`${testid}-empty`}>
      <CheckCircle size={20} className="text-emerald-600 shrink-0 mt-0.5" weight="fill" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-900">
          {message || "No actionable recommendations right now."}
        </div>
        <p className="text-xs text-slate-600 mt-0.5">
          {producers?.length
            ? <>All producers reported no urgent items. Complete today's Reconciliation for higher-confidence forecasts.</>
            : <>The unified engine is warming up. Try refreshing in a moment.</>}
        </p>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Producer status strip (footer)
// ---------------------------------------------------------------------------
function ProducerStatus({ data, testid }) {
  const list = (data.producers_used || []).map((p) => ({ name: p, ok: true }));
  for (const e of (data.producer_errors || [])) list.push({ name: e.producer, ok: false });
  if (!list.length) return null;
  return (
    <div
      className="px-5 py-2 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-600 flex items-center gap-2 flex-wrap"
      data-testid={testid}
    >
      <Info size={11} className="text-slate-400" />
      <b className="text-slate-500 uppercase tracking-wider">Producers</b>
      {list.map((p) => (
        <span
          key={p.name}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${p.ok ? "border-slate-200 bg-white" : "border-rose-200 bg-rose-50 text-rose-900"}`}
        >
          {p.ok ? <CheckCircle size={10} className="text-emerald-600" weight="fill" /> : <Info size={10} className="text-rose-600" />}
          {PRODUCER_LABEL[p.name] || p.name}
        </span>
      ))}
      <span className="ml-auto text-slate-400">as of {new Date(data.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
    </div>
  );
}
